/**
 * src/shell/auth/services/IdleTimeoutManager.ts
 *
 * Idle Timeout & Session Activity Monitor.
 * Uses Page Visibility API + throttled pointer/keyboard/touch interactions.
 * Emits idleWarning (30s prior) and idleLock events to the Auth State Machine.
 */

import { EventEmitter } from '../../utils/event-emitter';

export type TimeoutPreset = '5m' | '15m' | '30m' | '1h' | 'never';

const PRESET_SECONDS: Record<TimeoutPreset, number> = {
  '5m': 5 * 60,
  '15m': 15 * 60,
  '30m': 30 * 60,
  '1h': 60 * 60,
  never: 0,
};

export interface IdleTimeoutConfig {
  preset: TimeoutPreset;
  customSeconds?: number;
  warningLeadSeconds?: number; // default 30s
}

export class IdleTimeoutManager extends EventEmitter {
  private static instance: IdleTimeoutManager;
  private config: IdleTimeoutConfig = { preset: '15m', warningLeadSeconds: 30 };
  private timeoutSeconds = 15 * 60;
  private lastActivity = Date.now();
  private checkInterval: NodeJS.Timeout | null = null;
  private warningEmitted = false;
  private isPaused = false;
  private throttleTimer = 0;

  private constructor() {
    super();
    this.updateTimeoutSeconds();
    this.bindActivityListeners();
    this.startMonitor();
  }

  public static getInstance(): IdleTimeoutManager {
    if (!IdleTimeoutManager.instance) {
      IdleTimeoutManager.instance = new IdleTimeoutManager();
    }
    return IdleTimeoutManager.instance;
  }

  public setConfig(cfg: Partial<IdleTimeoutConfig>): void {
    this.config = { ...this.config, ...cfg };
    this.updateTimeoutSeconds();
    this.resetActivity();
  }

  public getConfig(): IdleTimeoutConfig {
    return { ...this.config };
  }

  public resetActivity = (): void => {
    const now = Date.now();
    // Throttle activity updates to once per 500ms
    if (now - this.throttleTimer < 500) return;
    this.throttleTimer = now;

    this.lastActivity = now;
    if (this.warningEmitted) {
      this.warningEmitted = false;
      this.emit('idle:activityRestored');
    }
  };

  public pause(): void {
    this.isPaused = true;
  }

  public resume(): void {
    this.isPaused = false;
    this.resetActivity();
  }

  public destroy(): void {
    if (this.checkInterval) clearInterval(this.checkInterval);
    if (typeof window !== 'undefined') {
      window.removeEventListener('mousemove', this.resetActivity);
      window.removeEventListener('keydown', this.resetActivity);
      window.removeEventListener('touchstart', this.resetActivity);
      window.removeEventListener('pointerdown', this.resetActivity);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  private updateTimeoutSeconds(): void {
    if (this.config.customSeconds !== undefined && this.config.customSeconds > 0) {
      this.timeoutSeconds = this.config.customSeconds;
    } else {
      this.timeoutSeconds = PRESET_SECONDS[this.config.preset] || 15 * 60;
    }
  }

  private bindActivityListeners(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('mousemove', this.resetActivity, { passive: true });
      window.addEventListener('keydown', this.resetActivity, { passive: true });
      window.addEventListener('touchstart', this.resetActivity, { passive: true });
      window.addEventListener('pointerdown', this.resetActivity, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  private handleVisibilityChange = (): void => {
    if (typeof document !== 'undefined' && !document.hidden) {
      this.resetActivity();
    }
  };

  private startMonitor(): void {
    if (this.checkInterval) clearInterval(this.checkInterval);
    this.checkInterval = setInterval(() => {
      this.checkIdleStatus();
    }, 1000);
  }

  public checkIdleStatus(): void {
    if (this.isPaused || this.timeoutSeconds === 0) return;

    const idleSeconds = Math.floor((Date.now() - this.lastActivity) / 1000);
    const warningLead = this.config.warningLeadSeconds || 30;

    // Check warning threshold
    if (idleSeconds >= this.timeoutSeconds - warningLead && !this.warningEmitted && idleSeconds < this.timeoutSeconds) {
      this.warningEmitted = true;
      const remaining = Math.max(1, this.timeoutSeconds - idleSeconds);
      this.emit('idle:warning', { remainingSeconds: remaining });
    }

    // Check lock threshold
    if (idleSeconds >= this.timeoutSeconds) {
      this.warningEmitted = false;
      this.emit('idle:lock', { idleSeconds });
      this.pause(); // Pause until unlocked
    }
  }
}

export const idleTimeoutManager = IdleTimeoutManager.getInstance();
